import React, { useCallback, useState } from 'react';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box } from '@mui/material';

import useHubTranslation from '@/utils/i18n/useHubTranslation';
import SidebarBase from '@/ui/Layout/Sidebar/SidebarBase';
import StyledNavContainer from '@/ui/Layout/Sidebar/StyledComponents/StyledNavContainer';
import StyledToggleButton from '@/ui/Layout/Sidebar/StyledComponents/StyledToggleButton';

interface SidebarNavigationProps {
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onExpandSidebar?: () => void;
  width?: number;
  collapsedWidth?: number;
  showToggleButton?: boolean;
}

export const useSidebarNavigation = (
  initialCollapsed = false
): {
  isCollapsed: boolean;
  toggleCollapse: () => void;
  collapse: () => void;
  expand: () => void;
} => {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const collapse = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  const expand = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  return {
    isCollapsed,
    toggleCollapse,
    collapse,
    expand,
  };
};

export const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  isCollapsed = false,
  onToggleCollapse,
  onExpandSidebar,
  width = 275,
  collapsedWidth = 60,
  showToggleButton = true,
}) => {
  const { translateHubUi } = useHubTranslation();
  const navWidth = isCollapsed ? collapsedWidth : width;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        minHeight: 0,
        zIndex: 2,
      }}
    >
      <StyledNavContainer navWidth={navWidth} sx={{ position: 'relative' }}>
        <SidebarBase
          collapsed={isCollapsed}
          onExpandSidebar={onExpandSidebar}
        />
        {showToggleButton && onToggleCollapse && (
          <StyledToggleButton
            isCollapsed={isCollapsed}
            onClick={onToggleCollapse}
            aria-label={
              isCollapsed
                ? translateHubUi(
                    'nav.sidebar.expandNavigation',
                    'Expand navigation'
                  )
                : translateHubUi(
                    'nav.sidebar.collapseNavigation',
                    'Collapse navigation'
                  )
            }
          >
            {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </StyledToggleButton>
        )}
      </StyledNavContainer>
    </Box>
  );
};

export default SidebarNavigation;
